import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV25TurboI2vParams {
  /**
   * 输入图像，支持URL（http://或https://开头）或Base64格式，与image_tail至少提供一个
   */
  image?: string;
  /**
   * 视频生成提示词，可选参数，最大长度2500字符
   */
  prompt?: string;
  /**
   * 视频时长，必填参数，支持: "5"（5秒）或 "10"（10秒），也支持整数5或10
   * @values 5 | 10
   */
  duration: '5' | '10';
  /**
   * 视频宽高比，可选参数，支持: 16:9, 9:16, 1:1
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 生成模式，可选参数，支持: std（标准）, pro（专业）
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 尾帧图像，可选参数，支持URL或Base64格式，与image至少提供一个
   */
  image_tail?: string;
  /**
   * 静态遮罩，可选参数
   */
  static_mask?: string;
  /**
   * 动态遮罩列表，可选参数
   */
  dynamic_masks?: string[];
  /**
   * 相机控制参数，可选参数
   */
  camera_control?: Record<string, any>;
  /**
   * 外部任务ID，可选参数
   */
  external_task_id?: string;
}

export interface KlingV25TurboI2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v2_5_turbo_i2v
 *
 * Kling AI V2.5 Turbo Image-to-Video Generator
 * 
 * Image-to-video generation implementation for Kling AI V2.5 Turbo API.
 * 
 * Model: kling-v2-5-turbo
 * Features: 图像到视频生成，支持首尾帧控制、静态遮罩、动态遮罩、相机控制
 * Duration: 5秒或10秒
 * Aspect Ratio: 16:9, 9:16, 1:1
 * Mode: std（标准）, pro（专业）
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV25TurboI2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV25TurboI2v({
 *   // 参数见 KlingV25TurboI2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV25TurboI2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingV25TurboI2v(
  params: KlingV25TurboI2vParams,
): Promise<KlingV25TurboI2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v2_5_turbo_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV25TurboI2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
